// frontend/src/pages/Profile/components/HostexPropertySelector.tsx
import React, { useState, useMemo } from 'react';
import {
  Card,
  Select,
  Button,
  Grid,
  Image,
  Stack,
  Group,
  Text,
  Title,
  Badge,
  Divider,
  Center,
  ActionIcon,
  Tooltip,
  Checkbox,
  useMantineTheme,
} from '@mantine/core';
import {
  IconLink,
  IconLinkOff,
  IconCheck,
  IconAlertCircle,
  IconFilter,
  IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mantine/hooks';
import { HostexProperty, MyHostexProperty } from '../../../api/integrations.api';

interface Props {
  hostexProperties: HostexProperty[];
  myProperties: MyHostexProperty[];
  onLink: (propertyId: number, hostexPropertyId: number) => Promise<void>;
  onUnlink: (propertyId: number) => Promise<void>;
  onTriggerSync?: (propertyId: number) => Promise<void>;
}

const HostexPropertySelector: React.FC<Props> = ({
  hostexProperties,
  myProperties,
  onLink,
  onUnlink,
  onTriggerSync,
}) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [selectedMyProperty, setSelectedMyProperty] = useState<number | null>(null);
  const [selectedHostex, setSelectedHostex] = useState<number | null>(null);
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState<number | null>(null);
  const [syncing, setSyncing] = useState<number | null>(null);

  const [selectedPartner, setSelectedPartner] = useState<string | null>(null);
  const [hideConnected, setHideConnected] = useState(false);

  const partners = useMemo(() => {
    const partnerMap = new Map<number, string>();
    myProperties.forEach(p => {
      if (p.partner_id && p.partner_name) {
        partnerMap.set(p.partner_id, p.partner_name);
      }
    });
    return Array.from(partnerMap.entries()).map(([id, name]) => ({
      value: id.toString(),
      label: name,
    }));
  }, [myProperties]);

  const connectedHostexIds = useMemo(() => {
    const set = new Set<number>();
    myProperties.forEach(p => {
      if (p.hostex_property_id) set.add(p.hostex_property_id);
    });
    return set;
  }, [myProperties]);

  const selectedMyData = myProperties.find(p => p.id === selectedMyProperty);
  const selectedHostexData = hostexProperties.find(p => p.id === selectedHostex);

  const handleLink = async () => {
    if (!selectedMyProperty || !selectedHostex) {
      notifications.show({
        title: t('common.warning'),
        message: t('integrations.hostex.selectAllFields'),
        color: 'orange',
        icon: <IconAlertCircle size={18} />,
      });
      return;
    }
    try {
      setLinking(true);
      await onLink(selectedMyProperty, selectedHostex);
      setSelectedMyProperty(null);
      setSelectedHostex(null);
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async (propertyId: number) => {
    try {
      setUnlinking(propertyId);
      await onUnlink(propertyId);
    } finally {
      setUnlinking(null);
    }
  };

  const handleTriggerSync = async (propertyId: number) => {
    if (!onTriggerSync) return;
    try {
      setSyncing(propertyId);
      await onTriggerSync(propertyId);
    } finally {
      setSyncing(null);
    }
  };

  const filteredProperties = useMemo(() => {
    let filtered = myProperties;
    if (selectedPartner) {
      filtered = filtered.filter(p => p.partner_id?.toString() === selectedPartner);
    }
    return filtered;
  }, [myProperties, selectedPartner]);

  const syncedProperties = filteredProperties.filter(p => p.is_synced);
  const unsyncedProperties = filteredProperties.filter(p => !p.is_synced);

  return (
    <Stack gap="xl">
      {syncedProperties.length > 0 && (
        <>
          <div>
            <Title order={5}>{t('integrations.hostex.linkedProperties')}</Title>
          </div>

          <Stack gap="md">
            {syncedProperties.map(property => {
              const hostexProp = hostexProperties.find(p => p.id === property.hostex_property_id);
              return (
                <Card
                  key={property.id}
                  shadow="sm"
                  padding="md"
                  radius="md"
                  withBorder
                  style={{ transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}
                >
                  <Grid gutter="md" align="center">
                    <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                      {property.cover_photo ? (
                        <Image
                          src={property.cover_photo}
                          alt={property.property_name || property.property_number}
                          height={80}
                          radius="md"
                          fit="cover"
                        />
                      ) : (
                        <Center
                          h={80}
                          style={{
                            background: theme.colors.dark[6],
                            borderRadius: theme.radius.md,
                          }}
                        >
                          <Text size="xs" c="dimmed">
                            {t('common.noPhoto')}
                          </Text>
                        </Center>
                      )}
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                      <Stack gap={4}>
                        <Text fw={600} lineClamp={1}>
                          {property.property_name || property.property_number}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {property.property_number} • {property.region}
                        </Text>
                        <Group gap={4}>
                          <Badge size="sm" variant="light" color="blue">
                            {property.property_type}
                          </Badge>
                          <Badge size="sm" variant="outline">
                            {Math.round(property.bedrooms || 0)} BD
                          </Badge>
                          <Badge size="sm" variant="outline">
                            {Math.round(property.bathrooms || 0)} BA
                          </Badge>
                        </Group>
                      </Stack>
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, sm: 12, md: 1 }}>
                      <Center>
                        <IconLink size={24} color={theme.colors.green[6]} />
                      </Center>
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, sm: 8, md: 3 }}>
                      <Stack gap={4}>
                        <Text fw={600} lineClamp={1}>
                          {hostexProp?.title || `Hostex #${property.hostex_property_id}`}
                        </Text>
                        <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>
                          {t('integrations.hostex.synced')}
                        </Badge>
                        <Text size="xs" c="dimmed">
                          ID: {property.hostex_property_id}
                        </Text>
                        {hostexProp && hostexProp.channels.length > 0 && (
                          <Group gap={4} mt={2}>
                            {hostexProp.channels.slice(0, 4).map(c => (
                              <Badge key={c.listing_id} size="xs" variant="dot" color="grape">
                                {c.channel_type}
                              </Badge>
                            ))}
                            {hostexProp.channels.length > 4 && (
                              <Badge size="xs" variant="outline" color="gray">
                                +{hostexProp.channels.length - 4}
                              </Badge>
                            )}
                          </Group>
                        )}
                      </Stack>
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, sm: 4, md: 1 }}>
                      <Group gap="xs" justify="flex-end">
                        {onTriggerSync && (
                          <Tooltip label={t('integrations.hostex.syncNow')}>
                            <ActionIcon
                              color="blue"
                              variant="light"
                              size="lg"
                              loading={syncing === property.id}
                              onClick={() => handleTriggerSync(property.id)}
                            >
                              <IconRefresh size={18} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        <Tooltip label={t('common.unlink')}>
                          <ActionIcon
                            color="red"
                            variant="light"
                            size="lg"
                            loading={unlinking === property.id}
                            onClick={() => handleUnlink(property.id)}
                          >
                            <IconLinkOff size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Grid.Col>
                  </Grid>
                </Card>
              );
            })}
          </Stack>
        </>
      )}

      {unsyncedProperties.length > 0 && (
        <>
          {syncedProperties.length > 0 && <Divider my="xl" />}
          <div>
            <Title order={5}>{t('integrations.hostex.linkNewProperty')}</Title>
          </div>

          <Card shadow="xs" padding="md" radius="md" withBorder bg={theme.colors.dark[7]}>
            <Group gap="lg" wrap="wrap">
              <Group gap="xs">
                <IconFilter size={18} color={theme.colors.gray[5]} />
                <Text size="sm" fw={500} c="dimmed">
                  {t('common.filters')}:
                </Text>
              </Group>

              {partners.length > 0 && (
                <Select
                  placeholder={t('integrations.hostex.selectPartner')}
                  value={selectedPartner}
                  onChange={setSelectedPartner}
                  clearable
                  size="sm"
                  data={partners}
                  style={{ minWidth: 200 }}
                />
              )}

              <Checkbox
                label={t('integrations.hostex.hideConnected')}
                checked={hideConnected}
                onChange={e => setHideConnected(e.currentTarget.checked)}
                size="sm"
              />
            </Group>
          </Card>

          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Stack gap="lg">
              <Grid gutter="md">
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Stack gap="sm">
                    <Text fw={600} size="sm">
                      {t('integrations.hostex.selectYourProperty')}
                    </Text>
                    <Select
                      placeholder={t('integrations.hostex.selectProperty')}
                      value={selectedMyProperty?.toString()}
                      onChange={value => setSelectedMyProperty(value ? parseInt(value) : null)}
                      searchable
                      clearable
                      data={unsyncedProperties.map(property => ({
                        value: property.id.toString(),
                        label: property.property_name
                          ? `${property.property_name} (${property.property_number})`
                          : property.property_number || `Property #${property.id}`,
                      }))}
                      styles={{ input: { fontSize: '16px' } }}
                    />

                    {selectedMyData && (
                      <Card shadow="xs" padding="sm" radius="md" withBorder>
                        {selectedMyData.cover_photo && (
                          <Image
                            src={selectedMyData.cover_photo}
                            alt={selectedMyData.property_name || selectedMyData.property_number}
                            radius="md"
                            mb="xs"
                            height={120}
                            fit="cover"
                          />
                        )}
                        <Stack gap={4}>
                          <Text size="xs" fw={600} lineClamp={1}>
                            {selectedMyData.property_name || selectedMyData.property_number}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {selectedMyData.property_number}
                          </Text>
                          <Group gap={4} mt={4}>
                            <Badge size="xs" variant="light">
                              {selectedMyData.property_type}
                            </Badge>
                            <Badge size="xs" variant="outline">
                              {Math.round(selectedMyData.bedrooms || 0)} BD
                            </Badge>
                            <Badge size="xs" variant="outline">
                              {Math.round(selectedMyData.bathrooms || 0)} BA
                            </Badge>
                          </Group>
                        </Stack>
                      </Card>
                    )}
                  </Stack>
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Stack gap="sm">
                    <Text fw={600} size="sm">
                      {t('integrations.hostex.selectHostexProperty')}
                    </Text>
                    <Select
                      placeholder={t('integrations.hostex.selectProperty')}
                      value={selectedHostex?.toString()}
                      onChange={value => setSelectedHostex(value ? parseInt(value) : null)}
                      searchable
                      clearable
                      disabled={!selectedMyProperty}
                      data={hostexProperties
                        .filter(p => !hideConnected || !connectedHostexIds.has(p.id))
                        .map(p => {
                          const isConnected = connectedHostexIds.has(p.id);
                          return {
                            value: p.id.toString(),
                            label: isConnected ? `✓ ${p.title}` : p.title,
                          };
                        })}
                      styles={{ input: { fontSize: '16px' }, option: { fontSize: '14px' } }}
                      renderOption={({ option }) => {
                        const id = parseInt(option.value);
                        const isConnected = connectedHostexIds.has(id);
                        const hp = hostexProperties.find(p => p.id === id);
                        return (
                          <Group gap="xs" justify="space-between" style={{ width: '100%' }}>
                            <Stack gap={2}>
                              <Text
                                size="sm"
                                style={{
                                  color: isConnected ? theme.colors.green[6] : undefined,
                                }}
                              >
                                {option.label}
                              </Text>
                              {hp && (
                                <Text size="xs" c="dimmed">
                                  {hp.channels.length} {t('integrations.hostex.channels')}
                                </Text>
                              )}
                            </Stack>
                            {isConnected && (
                              <Badge size="xs" color="green" variant="light">
                                {t('integrations.hostex.linked')}
                              </Badge>
                            )}
                          </Group>
                        );
                      }}
                    />

                    {selectedHostexData && (
                      <Card shadow="xs" padding="sm" radius="md" withBorder>
                        <Stack gap={4}>
                          <Text size="xs" fw={600} lineClamp={1}>
                            {selectedHostexData.title}
                          </Text>
                          <Text size="xs" c="dimmed">
                            ID: {selectedHostexData.id}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {selectedHostexData.channels.length}{' '}
                            {t('integrations.hostex.channels')}
                          </Text>
                          {selectedHostexData.channels.length > 0 && (
                            <Group gap={4} mt={4}>
                              {selectedHostexData.channels.map(c => (
                                <Badge
                                  key={c.listing_id}
                                  size="xs"
                                  variant="dot"
                                  color="grape"
                                >
                                  {c.channel_type}
                                </Badge>
                              ))}
                            </Group>
                          )}
                        </Stack>
                      </Card>
                    )}
                  </Stack>
                </Grid.Col>
              </Grid>

              <Button
                leftSection={<IconLink size={18} />}
                onClick={handleLink}
                loading={linking}
                disabled={!selectedMyProperty || !selectedHostex}
                size={isMobile ? 'md' : 'lg'}
                fullWidth
              >
                {t('integrations.hostex.linkButton')}
              </Button>
            </Stack>
          </Card>
        </>
      )}

      {unsyncedProperties.length === 0 && syncedProperties.length === 0 && (
        <Card shadow="sm" padding="xl" radius="md" withBorder>
          <Center>
            <Stack align="center" gap="md">
              <IconAlertCircle size={48} color={theme.colors.gray[5]} />
              <Text size="lg" c="dimmed">
                {t('integrations.hostex.noProperties')}
              </Text>
            </Stack>
          </Center>
        </Card>
      )}
    </Stack>
  );
};

export default HostexPropertySelector;
